/**
 * Admin Scroll Analytics Dashboard
 * Shows scroll depth engagement across public pages
 */
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  BarChart3,
  Eye,
  MousePointer2,
  Clock,
  TrendingUp,
  ArrowLeft,
  RefreshCw,
  Home,
  Building2,
  Users,
  Percent
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

export default function AdminScrollAnalytics() {
  const [data, setData] = useState({ pages: [] });
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    const token = localStorage.getItem("scaffoldx_token");
    
    try {
      const res = await fetch(`${API}/api/analytics/scroll-summary`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (res.ok) {
        const result = await res.json();
        setData(result);
        setLastUpdate(new Date());
      } else {
        toast.error("Kunne ikke hente analytikk data");
      }
    } catch (error) {
      console.error("Failed to fetch analytics:", error);
      toast.error("Feil ved henting av data");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    // Auto refresh every 30 seconds
    const interval = setInterval(fetchData, 30000);
    return () => clearInterval(interval);
  }, []);

  const getPageIcon = (page) => {
    if (page === "home") return <Home className="w-5 h-5 text-[#23F7DD]" />;
    if (page === "b2b") return <Building2 className="w-5 h-5 text-[#10B981]" />;
    if (page === "privat") return <Users className="w-5 h-5 text-[#F7931A]" />;
    return <Eye className="w-5 h-5 text-gray-400" />;
  };

  const getPageName = (page) => {
    if (page === "home") return "Forside (Landing)";
    if (page === "b2b") return "B2B Bedrift";
    if (page === "privat") return "Privat";
    return page;
  };

  const getScrollColor = (percent) => {
    if (percent >= 70) return "text-green-400";
    if (percent >= 50) return "text-yellow-400";
    if (percent >= 30) return "text-orange-400";
    return "text-red-400";
  };

  const getProgressWidth = (percent) => {
    return `${Math.min(percent, 100)}%`;
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Link to="/admin">
                <Button variant="ghost" size="sm" className="text-white/70 hover:text-white">
                  <ArrowLeft className="w-4 h-4 mr-1" />
                  Tilbake
                </Button>
              </Link>
            </div>
            <h1 className="text-2xl sm:text-3xl font-['Unbounded'] font-bold text-white flex items-center gap-3">
              <BarChart3 className="w-8 h-8 text-[#F7931A]" />
              Scroll Analytikk
            </h1>
            <p className="text-[#94A3B8] mt-1">
              Se hvor langt brukere scroller på hver side (siste 30 dager)
            </p>
          </div>
          
          <div className="flex items-center gap-3">
            {lastUpdate && (
              <span className="text-[#64748B] text-xs">
                Oppdatert: {lastUpdate.toLocaleTimeString("no-NO")}
              </span>
            )}
            <Button 
              variant="outline" 
              size="sm" 
              onClick={fetchData}
              disabled={loading}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Oppdater
            </Button>
          </div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          {data.pages.map((page) => (
            <Card key={page.page} className="bg-white/5 border-white/10">
              <CardHeader className="pb-2">
                <CardTitle className="flex items-center gap-2 text-white text-lg">
                  {getPageIcon(page.page)}
                  {getPageName(page.page)}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-2">
                    <Eye className="w-4 h-4 text-[#94A3B8]" />
                    <span className="text-2xl font-bold text-white">{page.total_views || 0}</span>
                    <span className="text-[#64748B] text-sm">besøk</span>
                  </div>
                </div>
                
                {/* Scroll Progress Bars */}
                <div className="space-y-3">
                  {[
                    { label: "25%", value: page.scroll_25, color: "bg-yellow-500" },
                    { label: "50%", value: page.scroll_50, color: "bg-orange-500" },
                    { label: "75%", value: page.scroll_75, color: "bg-[#F7931A]" },
                    { label: "100%", value: page.scroll_100, color: "bg-green-500" }
                  ].map((milestone) => (
                    <div key={milestone.label} className="flex items-center gap-3">
                      <span className="text-[#64748B] text-xs w-10">{milestone.label}</span>
                      <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                        <div 
                          className={`h-full ${milestone.color} transition-all duration-500`}
                          style={{ width: getProgressWidth(milestone.value) }}
                        />
                      </div>
                      <span className={`text-sm font-medium w-12 text-right ${getScrollColor(milestone.value)}`}>
                        {milestone.value || 0}%
                      </span>
                    </div>
                  ))}
                </div>
                
                {/* Engagement Score */}
                <div className="mt-4 pt-4 border-t border-white/10">
                  <div className="flex items-center justify-between">
                    <span className="text-[#94A3B8] text-sm flex items-center gap-1">
                      <TrendingUp className="w-4 h-4" />
                      Fullført scroll
                    </span>
                    <Badge className={`${page.scroll_100 >= 50 ? 'bg-green-500/20 text-green-400' : page.scroll_100 >= 25 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-red-500/20 text-red-400'}`}>
                      {page.scroll_100 || 0}% leser hele siden
                    </Badge>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* No Data Message */}
        {data.pages.length === 0 && !loading && (
          <Card className="bg-white/5 border-white/10">
            <CardContent className="py-16 text-center">
              <BarChart3 className="w-16 h-16 text-[#64748B] mx-auto mb-4 opacity-30" />
              <h3 className="text-xl font-bold text-white mb-2">Ingen data ennå</h3>
              <p className="text-[#94A3B8] max-w-md mx-auto">
                Scroll-analytikk samles inn automatisk når brukere besøker de offentlige sidene.
                Data vil vises her etter at noen har besøkt sidene.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Legend / Info */}
        <Card className="bg-white/5 border-white/10 mt-6">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-yellow-500" />
                <span className="text-[#94A3B8]">25% - Start scroll</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-orange-500" />
                <span className="text-[#94A3B8]">50% - Halvveis</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-[#F7931A]" />
                <span className="text-[#94A3B8]">75% - Nesten ferdig</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-3 h-3 rounded-full bg-green-500" />
                <span className="text-[#94A3B8]">100% - Fullført</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tips */}
        <div className="mt-6 p-4 bg-[#F7931A]/10 border border-[#F7931A]/30 rounded-lg">
          <h4 className="text-[#F7931A] font-bold mb-2 flex items-center gap-2">
            <MousePointer2 className="w-4 h-4" />
            Tips for bedre engagement
          </h4>
          <ul className="text-[#94A3B8] text-sm space-y-1">
            <li>• Lav scroll til 50%? Flytt viktig innhold høyere opp</li>
            <li>• Lav scroll til 100%? Vurder å forkorte siden eller legge til mer engasjerende innhold</li>
            <li>• Høy bounce på mobil? Sjekk at siden er mobilvennlig</li>
          </ul>
        </div>
      </div>

      {/* Footer */}
      <footer className="mt-8 border-t border-white/10 py-4 px-6">
        <div className="max-w-6xl mx-auto flex items-center justify-between text-sm text-[#64748B]">
          <span>AllStillas Analytics</span>
          <Link to="/admin" className="hover:text-white transition-colors">
            ← Tilbake til admin
          </Link>
        </div>
      </footer>
    </div>
  );
}
